import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { Portal } from '@frontend/components';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { useInstanceId, useNamespacedNavigate, useObjectTitle } from '@frontend/hooks';
import { AgentExecutionItem } from '@frontend/types/agentExecution';
import { WorkflowAgentExecution } from '@frontend/types/workflow-agent';

import { useAgentExecutions } from '../../hooks/useAgentExecutions';
import { useGetWorkflowAgentExecution } from '../../hooks/useWorkflowAgent';
import { WorkflowExecutionContent } from '../workflow-execution';

import { ExecutionDetailsSidebar } from './ExecutionDetailsSidebar';
import { ExecutionsTable } from './ExecutionsTable';

// Convert WorkflowAgentExecution to AgentExecutionItem format
const convertToAgentExecutionItem = (execution: WorkflowAgentExecution): AgentExecutionItem => {
  return {
    id: execution.id,
    workflow_agent_id: execution.workflow_agent_id,
    status: execution.status as AgentExecutionItem['status'],
    start_time: execution.start_time,
    end_time: execution.end_time || undefined,
    trigger_id: execution.trigger_id,
    user_id: execution.user_id,
    links: {
      self: execution.links.self,
      workflow_agent: execution.links.workflow_agent,
    },
  };
};

type AgentExecutionsTabProps = {
  agentId?: string;
  backHref?: string;
  executionBasePath?: string;
};

export function AgentExecutionsTab({
  agentId,
  backHref,
  executionBasePath = '/agents/executions',
}: AgentExecutionsTabProps) {
  useObjectTitle('Workflow Logs');
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNamespacedNavigate();
  const { instanceId } = useInstanceId();
  const [workflowAgentFeature] = useDecision('workflow-agent');
  const [selectedExecution, setSelectedExecution] = useState<AgentExecutionItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, isPending, error } = useAgentExecutions({
    instanceId: instanceId || '',
    agentId,
    page: currentPage,
    pageSize,
  });

  // Fetch execution by ID if URL param is present
  const { data: executionById } = useGetWorkflowAgentExecution(executionId);

  // Convert fetched execution to AgentExecutionItem format
  const executionFromUrl = useMemo(() => {
    if (!executionById) return null;
    return convertToAgentExecutionItem(executionById);
  }, [executionById]);

  const handleExecutionSelect = (execution: AgentExecutionItem) => {
    setSelectedExecution(execution);
  };

  const handleCloseSidebar = () => {
    setSelectedExecution(null);
  };

  const handleWorkflowExecutionView = (execution: AgentExecutionItem) => {
    if (!workflowAgentFeature.enabled) {
      return;
    }
    navigate(`${executionBasePath}/${execution.id}`);
  };

  const handleClosePortal = useCallback(() => {
    navigate(backHref ?? '/agents/executions');
  }, [backHref, navigate]);

  // Filter executions based on feature flag
  const filteredExecutions = useMemo(() => {
    if (!data?.items) return [];

    if (!workflowAgentFeature.enabled) {
      // Filter out workflow executions when feature flag is disabled
      return data.items.filter(execution => !execution.workflow_agent_id);
    }

    return data.items;
  }, [data?.items, workflowAgentFeature.enabled]);

  return (
    <Flex h="full" w="full">
      <ExecutionsTable
        executions={filteredExecutions}
        pagination={data?.pagination}
        isLoading={isPending}
        error={error}
        onExecutionSelect={handleExecutionSelect}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onWorkflowExecutionView={handleWorkflowExecutionView}
      />

      <Sidebar open={!!selectedExecution} dock="right" initialWidth={500}>
        {selectedExecution && (
          <ExecutionDetailsSidebar execution={selectedExecution} onClose={handleCloseSidebar} />
        )}
      </Sidebar>

      <Portal isOpen={!!executionId && !!executionFromUrl} onClose={handleClosePortal}>
        {executionFromUrl && workflowAgentFeature.enabled && (
          <WorkflowExecutionContent execution={executionFromUrl} onClose={handleClosePortal} />
        )}
      </Portal>
    </Flex>
  );
}
